<script lang="ts">
    import type { Lead, Customer } from "@/types/types";
    import { Building2, Mail, Phone, DollarSign, Locate, Calendar, ClipboardList, CheckCircle2, XCircle, X, CheckCheck } from 'lucide-svelte';
    import { formatDateLocale } from '@/lib/dateUtils';
    import { router } from '@inertiajs/svelte';
    import { toast } from 'svelte-sonner';

    type Props = {
        lead: Lead;
        ondragstart?: (event: DragEvent) => void;
        ondrag?: (event: DragEvent) => void;
        ondragend?: (event: DragEvent) => void;
        onclick?: (event: MouseEvent) => void;
        statusClass?: string;
    };

    let { lead, ondragstart, ondrag, ondragend, onclick, statusClass }: Props = $props();

    // Find the primary customer from the customers array
    const primaryCustomer = $derived(() => {
        if (!lead.customers || !Array.isArray(lead.customers)) return null;
        return lead.customers.find((c: Customer & { pivot?: { is_primary?: boolean } }) => c.pivot?.is_primary) || null;
    });

    // Determine if primary customer is an organisation
    const isOrganisation = $derived(() => {
        const customer = primaryCustomer();
        return customer?.type === 'organisation';
    });

    // Get the display name for the card header
    const displayName = $derived(() => {
        const customer = primaryCustomer();
        if (isOrganisation() && customer?.organisation_name) {
            return customer.organisation_name;
        }
        // Fall back to lead's first_name/last_name
        if(lead.organisation_name != null) {
            return  lead.organisation_name
        }
        return `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
    });

    let isDragging = $state(false);
    // eslint-disable-next-line svelte/prefer-writable-derived
    let invoiced = $state(lead.invoiced ?? false);

    $effect(() => {
        invoiced = lead.invoiced ?? false;
    });

    function toggleInvoiced(event: MouseEvent) {
        event.stopPropagation();
        const next = !invoiced;
        invoiced = next;
        router.patch(route('leads.toggle-invoiced', { lead: lead.id }), {}, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(next ? 'Marked as invoiced' : 'Marked as not invoiced');
            },
            onError: () => {
                invoiced = !next;
                toast.error('Failed to update invoice status');
            },
        });
    }

    function formatCurrency(value: number | null | undefined) {
        if (!value) return '';
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
        }).format(value);
    }

    function handleDragStart(event: DragEvent) {
        isDragging = true;

        // Create a clean drag ghost outside the overflow container to avoid clipping
        if (event.dataTransfer && event.target instanceof HTMLElement) {
            const clone = event.target.cloneNode(true) as HTMLElement;
            clone.style.position = 'absolute';
            clone.style.top = '-9999px';
            clone.style.left = '-9999px';
            clone.style.width = `${event.target.offsetWidth}px`;
            clone.style.opacity = '0.9';
            document.body.appendChild(clone);
            event.dataTransfer.setDragImage(clone, event.offsetX, event.offsetY);
            // Clean up after the browser has captured the image
            requestAnimationFrame(() => clone.remove());
        }

        ondragstart?.(event);
    }

    function handleDrag(event: DragEvent) {
        ondrag?.(event);
    }

    function handleDragEnd(event: DragEvent) {
        ondragend?.(event);
        // Small delay to prevent click from firing after drag
        setTimeout(() => {
            isDragging = false;
        }, 100);
    }

    function handleClick(event: MouseEvent) {
        if (!isDragging) {
            onclick?.(event);
        }
    }

    const computedStatusClass = $derived(statusClass ?? '');

    // Check site survey status
    const siteSurveyStatus = $derived(() => {
        if (!lead.survey_required) return 'not_required';
        if (!lead.has_site_survey) return 'incomplete';
        return 'complete';
    });
</script>

<div
    role="button"
    tabindex="0"
    class="lead-card {computedStatusClass}"
    draggable="true"
    data-lead-id={lead.id}
    ondragstart={handleDragStart}
    ondrag={handleDrag}
    ondragend={handleDragEnd}
    onclick={handleClick}
    onkeydown={(e) => e.key === 'Enter' && handleClick(e as any)}
>
    <div class="lead-header" class:has-toggle={lead.status === 'invoiced'}>
        <div class="lead-header-text">
            {#if lead.reference}
                <span class="lead-ref">{lead.reference}</span>
            {/if}
            <h4 class="lead-name">{displayName()} {#if lead.address?.postcode} - {lead.address.postcode}{/if}</h4>
        </div>
        {#if lead.status === 'invoiced'}
            <div class="invoiced-toggle-wrapper">
            <span class="invoiced-label">Invoiced</span>
            <button
                class="invoiced-toggle"
                class:active={invoiced}
                onclick={toggleInvoiced}
                title={invoiced ? 'Mark as not invoiced' : 'Mark as invoiced'}
                aria-label={invoiced ? 'Mark as not invoiced' : 'Mark as invoiced'}
                aria-pressed={invoiced}
            >
                <span class="toggle-track">
                    <span class="toggle-thumb"></span>
                </span>
                <span class="toggle-icon">
                    {#if invoiced}
                        <CheckCheck size={13} />
                    {:else}
                        <X size={13} />
                    {/if}
                </span>
            </button>
            </div>
        {/if}
    </div>

    {#if lead.address?.postcode}
        <div class="lead-detail">
            <Locate size={14} />
            <span> {lead.address.address_line_one } {lead.address.city}</span>
        </div>
    {/if}

    {#if lead.estimated_job_start}
        <div class="lead-detail">
            <Calendar size={14} />
            <span> {formatDateLocale(lead.estimated_job_start, true) } - {formatDateLocale(lead.estimated_job_end, true)}</span>
        </div>
    {/if}

    {#if lead.estimated_value}
        <div class="lead-detail">
            <DollarSign size={14} />
            <span class="font-semibold">{formatCurrency(lead.estimated_value)}</span>
        </div>
    {/if}

    <!-- Site Survey Status -->
    <div class="lead-detail site-survey-status" class:complete={siteSurveyStatus() === 'complete'} class:incomplete={siteSurveyStatus() === 'incomplete'} class:not-required={siteSurveyStatus() === 'not_required'}>
        {#if siteSurveyStatus() === 'complete'}
            <CheckCircle2 size={14} />
            <span>Survey Complete</span>
        {:else if siteSurveyStatus() === 'not_required'}
            <ClipboardList size={14} />
            <span>Survey Not Required</span>
        {:else}
            <XCircle size={14} />
            <span>Survey Incomplete</span>
        {/if}
    </div>

    <!-- Expandable details (shown on hover) -->
    <div class="expandable-details">
        <div class="expandable-content">
            {#if lead.organisation_name}
                <div class="lead-detail">
                    <Building2 size={14} />
                    <span>{lead.organisation_name}</span>
                </div>
            {/if}

            {#if lead.email}
                <div class="lead-detail">
                    <Mail size={14} />
                    <span class="truncate">{lead.email}</span>
                </div>
            {/if}

            {#if lead.phone}
                <div class="lead-detail">
                    <Phone size={14} />
                    <span>{lead.phone}</span>
                </div>
            {/if}
        </div>
    </div>
</div>

<style lang="scss">
    .lead-card {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 0.75rem;
        background: var(--clr-surface-1);
        border: 1px solid var(--clr-border);
        border-radius: 6px;
        cursor: grab;
        transition: all 200ms;
        text-decoration: none;
        color: inherit;

        &:hover {
            background: var(--clr-surface-3);
            border-color: var(--clr-primary);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        &:active {
            cursor: grabbing;
        }

        /* Status-specific colors (semi-translucent) */
        &.status-new {
            background: hsl(var(--status-new) / 0.2);
            border-color: hsl(var(--status-new) / 0.5);
        }

        &.status-needs-survey {
            background: hsl(var(--status-needs-survey) / 0.3);
            border-color: hsl(var(--status-needs-survey) / 0.6);
        }

        &.status-needs-pricing {
            background: hsl(var(--status-needs-pricing) / 0.2);
            border-color: hsl(var(--status-needs-pricing) / 0.5);
        }

        &.status-tendered {
            background: hsl(var(--status-tendered) / 0.2);
            border-color: hsl(var(--status-tendered) / 0.5);
        }

        &.status-closed-won {
            background: hsl(var(--status-closed-won) / 0.25);
            border-color: hsl(var(--status-closed-won) / 0.5);
        }

        &.status-booked {
            background: hsl(var(--status-booked) / 0.25);
            border-color: hsl(var(--status-booked) / 0.5);
        }

        &.status-invoiced {
            background: hsl(var(--status-invoiced) / 0.25);
            border-color: hsl(var(--status-invoiced) / 0.5);
        }

        &.status-extra-hire {
            background: hsl(var(--status-extra-hire) / 0.25);
            border-color: hsl(var(--status-extra-hire) / 0.5);
        }

        &.status-awaiting-takedown {
            background: hsl(var(--status-awaiting-takedown) / 0.25);
            border-color: hsl(var(--status-awaiting-takedown) / 0.5);
        }

        &.status-closed-lost {
            background: hsl(var(--status-closed-lost) / 0.25);
            border-color: hsl(var(--status-closed-lost) / 0.5);
        }
    }

    .lead-header {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;

        &.has-toggle {
            flex-direction: row;
            align-items: flex-start;
            justify-content: space-between;
            gap: 0.5rem;
        }
    }

    .lead-header-text {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
        flex: 1;
        min-width: 0;
    }

    .invoiced-toggle-wrapper {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        flex-shrink: 0;
    }

    .invoiced-label {
        font-size: 0.625rem;
        font-weight: 500;
        color: hsl(var(--clr-on-surface-2));
        white-space: nowrap;
    }

    .invoiced-toggle {
        display: flex;
        align-items: center;
        gap: 0.3rem;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        flex-shrink: 0;

        .toggle-track {
            position: relative;
            width: 2rem;
            height: 1.125rem;
            border-radius: 999px;
            background: hsl(var(--clr-border));
            transition: background 200ms;
            flex-shrink: 0;
        }

        .toggle-thumb {
            position: absolute;
            top: 2px;
            left: 2px;
            width: 0.875rem;
            height: 0.875rem;
            border-radius: 50%;
            background: white;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
            transition: transform 200ms;
        }

        .toggle-icon {
            display: flex;
            align-items: center;
            color: hsl(var(--clr-on-surface-2));
            transition: color 200ms;
        }

        &:hover .toggle-track {
            background: color-mix(in srgb, var(--success) 60%, transparent);
        }

        &.active {
            .toggle-track {
                background: var(--success);
            }
            .toggle-thumb {
                transform: translateX(0.875rem);
            }
            .toggle-icon {
                color: var(--success);
            }
        }
    }

    .lead-ref {
        font-size: 0.625rem;
        font-weight: 500;
        color: hsl(var(--clr-on-surface-2));
        font-family: monospace;
        letter-spacing: 0.025em;
    }

    .lead-name {
        font-size: 0.875rem;
        font-weight: 600;
        color: hsl(var(--clr-on-surface-1));
        margin: 0;
    }

    .lead-detail {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.75rem;
        color: hsl(var(--clr-on-surface-2));

        :global(svg) {
            flex-shrink: 0;
            opacity: 0.6;
        }

        span {
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .truncate {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .expandable-details {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 300ms ease, opacity 300ms ease;
        opacity: 0;
        overflow: hidden;
    }

    .expandable-content {
        min-height: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .lead-card:hover .expandable-details {
        grid-template-rows: 1fr;
        opacity: 1;
    }
</style>
